import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton, Chip,
  CircularProgress, Tooltip, Divider, Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { QRCodeSVG } from 'qrcode.react';
import { addDoc, collection, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { callClaude } from '../../utils/claudeProxy';
import { authFetch } from '../../utils/authFetch';

// ── AI text generation ───────────────────────────────────────────────────────

async function generateMailBody({ type, lead, companyName, ownerName, deptNames, userContext }) {

  const isPling = type !== 'kling-klang';
  const typeLabel = isPling ? 'PLING' : 'KLING-KLANG';
  const valueStr = lead.estimatedValue
    ? `Estimerat värde: ${Number(lead.estimatedValue).toLocaleString('sv-SE')} KSEK`
    : '';
  const deptStr = deptNames?.length ? `Affärsområde(n): ${deptNames.join(', ')}` : '';
  const reqStr = lead.mustRequirements?.length
    ? `Skakrav: ${lead.mustRequirements.slice(0, 5).join(', ')}`
    : '';

  const prompt = isPling
    ? `Du skriver ett internt ${typeLabel}-mail för konsultbolaget Tikab.
PLING är ett entusiastiskt, internt mail om ett nytt kvalificerat affärslead.
Mottagare är alla Tikabs medarbetare. Tonen ska vara energifylld, professionell med en touch av humor.

Scriv en e-posttext (INTE ämnesrad) på svenska, max 5 korta stycken.
Inkludera: vad uppdraget handlar om, vilken kund, varför det är spännande, vad vi kan bidra med och vem som ansvarar.
Avsluta med ett uppmaningsfullt kort stycke som engagerar kollegorna.

Lead: ${lead.title || ''}
Kund: ${companyName || 'okänd'}
Beskrivning: ${lead.briefingText || lead.description || ''}
Roll / typ: ${lead.role || lead.type || ''}
${valueStr}
${deptStr}
${reqStr}
Ansvarig: ${ownerName || 'okänd'}
${userContext ? `\nBakgrund och kontext från ansvarig:\n${userContext}` : ''}

Skriv mailkroppen direkt. Inga kommentarer, inga rubriker — bara brödtexten.`
    : `Du skriver ett internt ${typeLabel}-mail för konsultbolaget Tikab.
KLING-KLANG är ett festligt, firande internt mail om en vunnen affär.
Mottagare är alla Tikabs medarbetare. Tonen ska vara upprymd och firande!

Skriv en e-posttext (INTE ämnesrad) på svenska, max 5-6 korta stycken.
Inkludera: vilken affär vi vann, hos vilken kund, vad uppdraget innebär, värdet för Tikab, lyft fram ansvarig person.
Avsluta med ett uppmuntrande, firande stycke.

Lead: ${lead.title || ''}
Kund: ${companyName || 'okänd'}
Beskrivning: ${lead.briefingText || lead.description || ''}
${valueStr}
${deptStr}
Ansvarig: ${ownerName || 'okänd'}
${userContext ? `\nKontext och nya detaljer:\n${userContext}` : ''}

Skriv mailkroppen direkt. Inga kommentarer — bara brödtexten.`;

  try {
    const json = await callClaude({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 600,
      messages: [{ role: 'user', content: prompt }],
    });
    return json?.content?.[0]?.text?.trim() || null;
  } catch {
    return null;
  }
}

// ── Email preview ────────────────────────────────────────────────────────────

function EmailPreview({ isPling, subject, bodyText, imageUrl, shareUrl }) {
  const accent = isPling ? '#FF6B00' : '#2E7D32';
  const emoji  = isPling ? '🔔' : '🏆';
  const label  = isPling ? 'PLING!' : 'KLING-KLANG!';
  const lines  = (bodyText || '').split('\n').filter(Boolean);

  return (
    <Box sx={{ fontFamily: "'Segoe UI', Arial, sans-serif", bgcolor: '#f5f5f5', p: 2, borderRadius: 2, height: '100%', overflowY: 'auto' }}>
      <Box sx={{ maxWidth: 480, mx: 'auto', bgcolor: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        {/* Header */}
        <Box sx={{ bgcolor: accent, px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: '1.6rem', lineHeight: 1 }}>{emoji}</Typography>
          <Box>
            <Typography fontWeight={900} sx={{ color: '#fff', fontSize: '1.1rem', lineHeight: 1 }}>{label}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>LagbasN intern nyhet</Typography>
          </Box>
        </Box>
        {/* Body */}
        <Box sx={{ p: 3 }}>
          <Typography fontWeight={700} sx={{ fontSize: '1rem', borderBottom: `2px solid ${accent}`, pb: 1, mb: 2 }}>
            {subject || '(Ämnesrad visas här)'}
          </Typography>
          {imageUrl && (
            <Box component="img" src={imageUrl} alt="" sx={{ width: '100%', borderRadius: 1, mb: 2 }} />
          )}
          {lines.map((l, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 1.5, lineHeight: 1.7, color: '#222', fontSize: '0.88rem' }}>{l}</Typography>
          ))}
          {!lines.length && (
            <Typography variant="body2" sx={{ color: '#bbb', fontStyle: 'italic' }}>Skriv eller generera mailinnehåll...</Typography>
          )}
          {shareUrl && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                <Box component="a" href={shareUrl} sx={{
                  display: 'inline-block', bgcolor: accent, color: '#fff',
                  px: 3, py: 1.25, borderRadius: 1.5, textDecoration: 'none',
                  fontWeight: 700, fontSize: '0.85rem',
                }}>
                  Öppna lead →
                </Box>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <QRCodeSVG value={shareUrl} size={80} />
                <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
                  {shareUrl}
                </Typography>
              </Box>
            </>
          )}
          <Typography sx={{ fontSize: '0.65rem', color: '#ccc', mt: 2.5, textAlign: 'center' }}>
            LagbasN · lagbasn.se
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ── Draft helpers ─────────────────────────────────────────────────────────────

function draftDocId(leadId, type) {
  return `${leadId}_${type}`;
}

function formatDraftTime(date) {
  if (!date) return '';
  return date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
}

// ── Main dialog ──────────────────────────────────────────────────────────────

export default function PlingMailDialog({
  open, onClose, type, lead, leadId,
  companyName, ownerName, deptNames,
  allUsers, currentUser,
  previousPlingText,
}) {
  const isPling    = type !== 'kling-klang';
  const accent     = isPling ? '#FF6B00' : '#2E7D32';
  const headerLabel = isPling ? 'PLING!' : 'KLING-KLANG!';
  const defaultSubject = isPling
    ? `PLING | ${companyName || 'Kund'} - ${lead?.title || 'Projekt'}`
    : `KLING-KLANG | ${companyName || 'Kund'} - ${lead?.title || 'Projekt'}`;

  const [subject,      setSubject]      = useState('');
  const [bodyText,     setBodyText]     = useState('');
  const [userContext,  setUserContext]  = useState('');
  const [imageUrl,     setImageUrl]     = useState('');
  const [shareUrl,     setShareUrl]     = useState('');
  const [generating,   setGenerating]   = useState(false);
  const [sending,      setSending]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [recipients,   setRecipients]   = useState([]);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  // Load draft or set defaults when dialog opens
  useEffect(() => {
    if (!open) return;
    setRecipients((allUsers || []).filter(u => u.email));

    async function loadDraft() {
      if (!leadId) {
        applyDefaults();
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'plingDrafts', draftDocId(leadId, type)));
        if (snap.exists()) {
          const d = snap.data();
          setSubject(d.subject ?? defaultSubject);
          setBodyText(d.bodyText ?? '');
          setUserContext(d.userContext ?? '');
          setImageUrl(d.imageUrl ?? '');
          setShareUrl(d.shareUrl ?? `${window.location.origin}/crm/leads/${leadId}`);
          setDraftSavedAt(d.savedAt?.toDate?.() ?? null);
          return;
        }
      } catch (_) {}
      applyDefaults();
    }

    function applyDefaults() {
      setSubject(defaultSubject);
      setBodyText(previousPlingText && !isPling ? `${previousPlingText}\n\n---\n` : '');
      setUserContext('');
      setImageUrl('');
      setShareUrl(leadId ? `${window.location.origin}/crm/leads/${leadId}` : '');
      setDraftSavedAt(null);
    }

    loadDraft();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleGenerate() {
    setGenerating(true);
    const text = await generateMailBody({ type, lead, companyName, ownerName, deptNames, userContext });
    if (text) setBodyText(text);
    setGenerating(false);
  }

  async function handleSaveDraft() {
    if (!leadId) return;
    setSaving(true);
    try {
      const now = new Date();
      await setDoc(doc(db, 'plingDrafts', draftDocId(leadId, type)), {
        leadId,
        type,
        subject,
        bodyText,
        userContext,
        imageUrl,
        shareUrl,
        savedAt: now,
        savedBy: currentUser?.uid || '',
      });
      setDraftSavedAt(now);
    } catch (e) {
      console.error('Kunde inte spara utkast:', e);
    } finally {
      setSaving(false);
    }
  }

  async function handleSend() {
    if (!bodyText.trim()) return;
    setSending(true);
    try {
      await authFetch('/.netlify/functions/pling-mail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          subject,
          bodyText,
          imageUrl: imageUrl || '',
          shareUrl: shareUrl || '',
          recipients: recipients.map(u => ({ name: u.displayName || u.name || u.email, email: u.email })),
        }),
      });

      await addDoc(collection(db, 'plingKlang'), {
        type,
        leadId:         leadId || '',
        leadTitle:      lead?.title || '',
        companyName:    companyName || '',
        estimatedValue: lead?.estimatedValue || null,
        departmentIds:  lead?.departmentIds || [],
        subject,
        aiText:         bodyText,
        fallbackText:   bodyText,
        imageUrl:       imageUrl || '',
        shareUrl:       shareUrl || '',
        recipientCount: recipients.length,
        createdBy:      currentUser?.uid || '',
        createdAt:      new Date(),
      });

      // Delete draft after successful send
      if (leadId) {
        deleteDoc(doc(db, 'plingDrafts', draftDocId(leadId, type))).catch(() => {});
      }

      onClose('sent');
    } catch (e) {
      console.error('Kunde inte skicka:', e);
    } finally {
      setSending(false);
    }
  }

  const recipientCount = recipients.length;

  return (
    <Dialog open={open} onClose={() => onClose(null)} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '90vh' } }}>
      <DialogTitle sx={{ bgcolor: accent, color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
        {isPling
          ? <NotificationsActiveIcon sx={{ fontSize: 22 }} />
          : <CelebrationIcon sx={{ fontSize: 22 }} />}
        <Typography fontWeight={900} sx={{ fontSize: '1.1rem', flex: 1 }}>{headerLabel}</Typography>
        {draftSavedAt && (
          <Chip
            label={`Utkast sparat ${formatDraftTime(draftSavedAt)}`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600, fontSize: '0.68rem' }}
          />
        )}
        <Chip label={`${recipientCount} mottagare`} size="small"
          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.72rem' }} />
        <IconButton size="small" onClick={() => onClose(null)} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
        {/* ── LEFT: Composer ── */}
        <Box sx={{ width: '45%', flexShrink: 0, p: 2.5, overflowY: 'auto', borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Subject */}
          <TextField
            label="Ämnesrad"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            size="small" fullWidth
            InputProps={{ sx: { fontWeight: 700, fontSize: '0.85rem' } }}
          />

          {/* Background context */}
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75, fontSize: '0.62rem' }}>
              Bakgrund / indata till AI
            </Typography>
            <TextField
              multiline rows={3} fullWidth size="small"
              placeholder={isPling
                ? 'Beskriv affärsmöjligheten, varför den är intressant, vad vi kan bidra med...'
                : 'Beskriv hur affären vanns, vad uppdraget innebär, nästa steg...'}
              value={userContext}
              onChange={e => setUserContext(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
            />
            <Button
              size="small" variant="contained" fullWidth
              startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
              disabled={generating}
              onClick={handleGenerate}
              sx={{ mt: 1, textTransform: 'none', bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
              {generating ? 'Genererar...' : 'Generera med AI'}
            </Button>
          </Box>

          {/* Mail body */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75, fontSize: '0.62rem' }}>
              Mailinnehåll
            </Typography>
            <TextField
              multiline fullWidth size="small"
              placeholder="Skriv eller generera mailtext..."
              value={bodyText}
              onChange={e => setBodyText(e.target.value)}
              minRows={8}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem', lineHeight: 1.7 } }}
            />
          </Box>

          {/* Image URL */}
          <TextField
            label="Bild-URL (valfri)"
            value={imageUrl}
            onChange={e => setImageUrl(e.target.value)}
            size="small" fullWidth
            placeholder="https://..."
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem' } }}
          />

          {/* Lead URL */}
          <TextField
            label="Lead-länk (QR & CTA)"
            value={shareUrl}
            onChange={e => setShareUrl(e.target.value)}
            size="small" fullWidth
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem' } }}
          />

          {/* Recipients summary */}
          <Box sx={{ bgcolor: '#f8f8f8', borderRadius: 1.5, p: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1, fontSize: '0.62rem' }}>
              Mottagare ({recipientCount} st)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 80, overflowY: 'auto' }}>
              {recipients.slice(0, 20).map(u => (
                <Tooltip key={u.id} title={u.email}>
                  <Avatar src={u.photoURL || u.photoUrl || undefined}
                    sx={{ width: 24, height: 24, fontSize: '0.6rem', bgcolor: accent, cursor: 'default' }}
                    imgProps={{ referrerPolicy: 'no-referrer' }}>
                    {(u.displayName || u.name || u.email || '?')[0].toUpperCase()}
                  </Avatar>
                </Tooltip>
              ))}
              {recipientCount > 20 && (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  +{recipientCount - 20} till
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* ── RIGHT: Preview ── */}
        <Box sx={{ flex: 1, bgcolor: '#f5f5f5', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #eee', bgcolor: '#fff' }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem' }}>
              Förhandsgranskning
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <EmailPreview
              isPling={isPling}
              subject={subject}
              bodyText={bodyText}
              imageUrl={imageUrl}
              shareUrl={shareUrl}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #eee', gap: 1 }}>
        <Button onClick={() => onClose(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Avbryt
        </Button>
        <Button
          variant="outlined" size="large"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          disabled={saving || !leadId}
          onClick={handleSaveDraft}
          sx={{ textTransform: 'none', borderColor: accent, color: accent, '&:hover': { borderColor: accent, bgcolor: `${accent}10` } }}>
          {saving ? 'Sparar...' : 'Spara utkast'}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained" size="large"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          disabled={!bodyText.trim() || !subject.trim() || sending || recipientCount === 0}
          onClick={handleSend}
          sx={{ textTransform: 'none', bgcolor: accent, '&:hover': { bgcolor: isPling ? '#e55a00' : '#1B5E20' }, fontWeight: 700 }}>
          {sending ? 'Skickar...' : `Skicka ${headerLabel} till ${recipientCount} medarbetare`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
